import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * Consistent page frame. Use for every top-level route content.
 *
 * <PageShell>
 *   <PageHeader title="Invoices" description="…">{actions}</PageHeader>
 *   <PageBody>…</PageBody>
 * </PageShell>
 */
export const PageShell = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // Layout owns width and gutters; the shell only stacks header + body.
      "w-full",
      className,
    )}
    {...props}
  />
))
PageShell.displayName = "PageShell"

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title: React.ReactNode
  description?: React.ReactNode
  breadcrumb?: React.ReactNode
  actions?: React.ReactNode
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ className, title, description, breadcrumb, actions, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6",
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1">
        {breadcrumb ? (
          <div className="mb-1 truncate text-xs text-muted-foreground">
            {breadcrumb}
          </div>
        ) : null}
        <h1 className="truncate text-2xl font-semibold leading-tight tracking-tight">
          {title}
        </h1>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions || children ? (
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {children}
        </div>
      ) : null}
    </div>
  ),
)
PageHeader.displayName = "PageHeader"

export const PageBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("space-y-4 sm:space-y-6", className)} {...props} />
))
PageBody.displayName = "PageBody"

/** Consistent card-y section. */
export const Section = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { title?: React.ReactNode; description?: React.ReactNode; actions?: React.ReactNode }
>(({ className, title, description, actions, children, ...props }, ref) => (
  <section
    ref={ref}
    className={cn(
      "rounded-xl border bg-card text-card-foreground shadow-xs",
      className,
    )}
    {...props}
  >
    {(title || actions) && (
      <header className="flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
        <div className="min-w-0">
          {title ? (
            <h2 className="text-base font-semibold leading-tight tracking-tight sm:text-lg">
              {title}
            </h2>
          ) : null}
          {description ? (
            <p className="text-xs text-muted-foreground sm:text-sm">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </header>
    )}
    <div className="px-4 py-4 sm:px-5 sm:py-5">{children}</div>
  </section>
))
Section.displayName = "Section"
